import styled from "@emotion/styled";
import { Alert, css, MobileStepper, Step, StepLabel, Stepper } from "@mui/material";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import type { ModalDialogProps } from "../dialogs";
import { ModalDialog } from "../dialogs";
import { FONT_WEIGHT_MEDIUM, FONT_WEIGHT_SEMI_BOLD } from "../utilities";
import { BackButton, CancelButton, WizardPageTitle } from "./shared";
import type { WizardPage } from "./types";

const HEIGHT = 360;
const CONTENT_WIDTH = 580;
const STEPPER_WIDTH = 260;

const SizingContainer = styled.div<{ $showStepPanel: boolean; $contentPxWidth?: number }>`
  overflow: hidden;
`;

const StepperContentContainer = styled.div<{
  $showStepPanel: boolean;
  $contentPxWidth?: number;
  $contentPxHeight?: number;
}>`
  display: flex;
  justify-content: center;

  width: ${({ $contentPxWidth, $showStepPanel }) =>
    $showStepPanel
      ? `${($contentPxWidth ?? CONTENT_WIDTH) + STEPPER_WIDTH}px`
      : `${$contentPxWidth ?? CONTENT_WIDTH}px`};

  --width: ${({ $contentPxWidth }) => `${$contentPxWidth ?? CONTENT_WIDTH}px`};
  --height: ${({ $contentPxHeight }) => $contentPxHeight ?? HEIGHT}px;

  min-height: ${HEIGHT}px;
`;

const StyledStepper = styled(Stepper)`
  flex: 0 0 ${STEPPER_WIDTH}px;

  padding: 20px;
  border-right: solid 1px rgba(0, 0, 0, 0.12);

  background: ${({ theme }) => theme.surfaceVariant};

  ${WizardPageTitle} {
    font-size: inherit;
    color: inherit;
    font-weight: inherit;
    margin-left: 5px;
    display: block;
  }

  .MuiStepConnector-vertical {
    flex: 0 0 30px;
  }
`;

const StyledStepLabel = styled(StepLabel)`
  font-size: ${({ theme }) => theme.fontSize};
  color: ${({ theme }) => theme.generalTextIcon};
  padding: 0;

  .Mui-active,
  &.Mui-active {
    font-weight: ${FONT_WEIGHT_SEMI_BOLD};
    color: ${({ theme }) => theme.primaryStrong};
  }
  .Mui-completed {
    font-weight: ${FONT_WEIGHT_MEDIUM};
    svg {
      color: ${({ theme }) => theme.primaryStrong};
    }
    span {
      color: ${({ theme }) => theme.generalTextIconVariant};
    }
  }
`;

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: var(--width);
  height: var(--height);

  overflow-x: hidden;
  overflow-y: auto;
`;

const ContentContainer = styled.div<{ steps: number }>`
  display: flex;
  flex-direction: row;

  width: calc(var(--width) * steps);
  height: 100%;
`;

const Content = styled.div`
  flex: none;
  width: var(--width);
  height: var(--height);
  overflow: hidden;
`;

const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100%;
  width: 100%;
`;

const StepperButtonContainer = styled.div`
  position: relative;

  display: flex;
  justify-content: flex-end;

  width: 100%;
`;

const StepperOverlayContainer = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;

  opacity: ${({ $show }) => ($show ? 1 : 0)};
  pointer-events: none;

  transition: opacity 300ms ease-in-out;

  & > div {
    border-radius: 50px;
  }
`;

const BackButtonContainer = styled.span<{ $available: boolean; $completing: boolean }>`
  margin-inline-start: auto;
  margin-inline-end: 8px;

  transition: opacity 0.5s ease-in-out;

  opacity: ${({ $available, $completing }) => ($available ? ($completing ? 0.3 : 1) : 0)};
  pointer-events: ${({ $available, $completing }) => ($available ? ($completing ? "none" : "auto") : "none")};
`;

const ButtonContainer = styled.span`
  display: flex;
  align-items: center;
  padding: 0 8px;
`;

const NextButtonContainer = styled.span<{ $available: boolean }>`
  transition: opacity 0.5s ease-in-out;

  opacity: ${({ $available }) => ($available ? 1 : 0.3)};
  pointer-events: ${({ $available }) => ($available ? "auto" : "none")};
`;

export type WizardProps = ModalDialogProps & {
  pages: WizardPage[];

  pageChangeDurationMs?: number;

  onBegin?: () => WizardPage["id"];
  onCancel: () => void;
  onComplete: () => Promise<void>;

  contentStyle?: CSSProperties;
  stepperStyle?: CSSProperties;

  stepPanel?: "vertical" | "horizontal" | "both" | "none";

  // If header is specified use that as the modal dialog title. Otherwise use the title of the current page.
  header?: ReactNode;
  errorMessage?: ReactNode;
};

const verifyAndExtractStylePxDimensions = (contentStyle: CSSProperties = {}) => {
  let { width, height } = contentStyle;

  if (width && typeof width === "string") {
    if (!width.trim().toLowerCase().endsWith("px")) throw new Error("Content style must specify width in pixels.");

    width = width.trim().slice(0, -2);
  }

  if (height && typeof height === "string") {
    if (!height.trim().toLowerCase().endsWith("px")) throw new Error("Content style must specify height in pixels.");

    height = height.trim().slice(0, -2);
  }

  return { contentPxWidth: width ? Number(width) : undefined, contentPxHeight: height ? Number(height) : undefined };
};

export const Wizard = ({
  pages,
  onBegin,
  onCancel,
  onComplete,
  contentStyle,
  stepperStyle,
  stepPanel = "none",
  errorMessage,
  header,
  pageChangeDurationMs = 300,
  ...rest
}: WizardProps) => {
  const [step, setStep] = useState(0);

  // onComplete is running...
  const [completing, setCompleting] = useState(false);
  // Automatically handle errors from onComplete()
  const [completeError, setCompleteError] = useState<string | null>(null);
  const headerErrorMessage = completeError ?? errorMessage;

  const [beginPage] = useState(() => (onBegin ? (pages.find(({ id }) => id === onBegin()) ?? pages[0]) : pages[0]));

  const availablePages = useMemo(
    () =>
      pages
        .slice(onBegin ? (pages.findIndex(({ id }) => id === onBegin()) ?? 0) : 0)
        .filter((page) => (beginPage.id === page.id || page.showCondition?.()) ?? true),
    [beginPage.id, onBegin, pages],
  );

  const reset = () => {
    setStep(0);
    setCompleteError(null);
    setCompleting(false);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleBack = useCallback(() => {
    if (step === 0) return;

    setStep(step - 1);
  }, [step]);

  const handleNext = useCallback(async () => {
    if (completing) return;

    if (step === availablePages.length - 1) {
      setCompleting(true);
      try {
        setCompleteError(null);
        await onComplete();
        // only reset on success
        reset();
      } catch (error) {
        // todo: better error
        setCompleteError(String(error));
      } finally {
        setCompleting(false);
      }
      return;
    }

    setStep(step + 1);
  }, [onComplete, availablePages, step, completing]);

  const { title, stepper } = availablePages[step] ?? pages[pages.length - 1];

  const nextAvailable = stepper.advanceCondition?.() ?? true;

  const { contentPxWidth, contentPxHeight } = verifyAndExtractStylePxDimensions(contentStyle);

  const handleKeyPress = useCallback(
    async (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && nextAvailable && !completing) {
        await handleNext();
      }
    },
    [handleNext, nextAvailable, completing],
  );

  return (
    <ModalDialog
      maxWidth={false}
      header={header ?? title}
      noPadding={true}
      onKeyPress={handleKeyPress}
      buttons={
        <StepperButtonContainer style={stepperStyle}>
          {!stepper.useBackAsCancel && stepper.cancel && (
            <ButtonContainer onClick={handleCancel} data-test="cancel-button-container">
              {stepper.cancel}
            </ButtonContainer>
          )}

          <BackButtonContainer
            $available={stepper.useBackAsCancel || step !== 0}
            $completing={completing}
            onClick={stepper.useBackAsCancel ? handleCancel : handleBack}
            data-test="back-button-container"
          >
            {stepper.back ?? (stepper.useBackAsCancel ? <CancelButton onClick={handleCancel} /> : <BackButton />)}
          </BackButtonContainer>

          <NextButtonContainer $available={nextAvailable} onClick={handleNext} data-test="next-button-container">
            {stepper.next}
          </NextButtonContainer>

          <StepperOverlayContainer
            $show={stepPanel === "horizontal" && availablePages.length > 1}
            style={{ ...stepperStyle, backgroundColor: "transparent" }}
          >
            <MobileStepper
              activeStep={step}
              steps={availablePages.length === 1 ? 0 : availablePages.length}
              nextButton={null}
              backButton={null}
              position="static"
              variant="dots"
            />
          </StepperOverlayContainer>
        </StepperButtonContainer>
      }
      actionsStyle={{ padding: stepperStyle ? 0 : "8px" }}
      {...rest}
      onClose={handleCancel}
    >
      <StepperContentContainer
        $showStepPanel={stepPanel === "vertical" || stepPanel === "both"}
        $contentPxWidth={contentPxWidth}
        $contentPxHeight={contentPxHeight}
      >
        {(stepPanel === "vertical" || stepPanel === "both") && (
          <StyledStepper orientation="vertical" activeStep={step}>
            {availablePages.map((page, index) => (
              <Step key={index}>
                <StyledStepLabel>{page.stepLabel ?? page.title}</StyledStepLabel>
              </Step>
            ))}
          </StyledStepper>
        )}
        <SizingContainer
          data-test="wizard-container"
          $showStepPanel={stepPanel === "vertical" || stepPanel === "both"}
          $contentPxWidth={contentPxWidth}
        >
          <DialogContainer>
            {headerErrorMessage && (
              <Alert
                severity="error"
                css={css`
                  white-space: pre-line;
                `}
              >
                {headerErrorMessage}
              </Alert>
            )}
            <ContentContainer
              steps={availablePages.length}
              style={{
                transform: `translateX(${-step * (contentPxWidth ?? CONTENT_WIDTH)}px)`,
                transition: `all ${pageChangeDurationMs}ms ease-in-out`,
              }}
              data-test="content-container"
            >
              {availablePages.map((page, index) => (
                <Content key={index} style={contentStyle} data-test="page">
                  <ScrollContainer>{page.content}</ScrollContainer>
                </Content>
              ))}
            </ContentContainer>
          </DialogContainer>
        </SizingContainer>
      </StepperContentContainer>
    </ModalDialog>
  );
};
